/**
 * Carbon Footprint Calculator Utility
 * Calculates CO2 emissions based on distance and vehicle type
 * 
 * Emission factors are based on industry standards for commercial vehicles
 * Values are in kg CO2 per kilometer
 */

class CarbonFootprintCalculator {
  constructor() {
    // Emission factors in kg CO2/km for different vehicle types
    this.emissionFactors = {
      // Light commercial vehicles
      'van': 0.185,
      'light_truck': 0.195,
      'pickup': 0.220,
      'small_van': 0.165,
      
      // Medium commercial vehicles  
      'truck': 0.280,
      'medium_truck': 0.315,
      'box_truck': 0.298,
      
      // Heavy commercial vehicles
      'heavy_truck': 0.425,
      'articulated_truck': 0.485,
      'large_truck': 0.465,
      
      // Specialized vehicles
      'delivery_vehicle': 0.175,
      'courier_vehicle': 0.155,
      'refrigerated_truck': 0.350,
      
      // Default fallback
      'default': 0.250,
      'unknown': 0.250
    };
    
    // Alternative calculation method: by engine size/capacity
    this.capacityBasedFactors = {
      // kg CO2/km based on vehicle capacity (tons)
      small: 0.165,    // < 2 tons
      medium: 0.285,   // 2-7 tons  
      large: 0.425,    // 7-15 tons
      xlarge: 0.485    // > 15 tons
    };
  }
  /**
   * Calculate carbon footprint for a single route
   * @param {number} distanceKm - Total distance in kilometers
   * @param {string|Array} vehicleType - Vehicle type(s) or array of vehicle data
   * @param {Object} options - Additional options
   * @returns {Object} Carbon footprint data
   */
  // eslint-disable-next-line no-unused-vars
  calculateRouteFootprint(distanceKm, vehicleType = 'default', options = {}) {
    if (!distanceKm || distanceKm <= 0) {
      return {
        totalCO2: 0,
        co2PerKm: 0,
        formatted: '0.0 kg CO₂',
        details: 'No distance data available'
      };
    }

    let emissionFactor;
    let vehicleInfo = '';

    // Handle array of vehicles (multi-vehicle route)
    if (Array.isArray(vehicleType)) {
      const avgEmissionFactor = this.calculateAverageEmissionFactor(vehicleType);
      emissionFactor = avgEmissionFactor.factor;
      vehicleInfo = avgEmissionFactor.info;
    } else {
      // Single vehicle or vehicle type string
      emissionFactor = this.getEmissionFactor(vehicleType);
      vehicleInfo = this.getVehicleTypeDescription(vehicleType);
    }

    const totalCO2 = distanceKm * emissionFactor;
    
    return {
      totalCO2: totalCO2,
      co2PerKm: emissionFactor,
      formatted: this.formatCO2Value(totalCO2),
      formattedPerKm: this.formatCO2Value(emissionFactor),
      vehicleInfo: vehicleInfo,
      distanceKm: distanceKm,
      details: `${vehicleInfo} • ${emissionFactor.toFixed(3)} kg CO₂/km`
    };
  }

  /**
   * Calculate average emission factor for multiple vehicles
   * @param {Array} vehicles - Array of vehicle objects
   * @returns {Object} Average emission factor and description
   */
  calculateAverageEmissionFactor(vehicles) {
    if (!vehicles || vehicles.length === 0) {
      return {
        factor: this.emissionFactors.default,
        info: 'Mixed vehicles (default)'
      };
    }

    let totalFactor = 0;
    const vehicleTypes = [];

    vehicles.forEach(vehicle => {
      const vehicleType = vehicle.type || vehicle.vehicleType || 'default';
      const factor = this.getEmissionFactor(vehicleType);
      totalFactor += factor;
      vehicleTypes.push(vehicleType);
    });

    const avgFactor = totalFactor / vehicles.length;
    const uniqueTypes = [...new Set(vehicleTypes)];
    
    return {
      factor: avgFactor,
      info: uniqueTypes.length === 1 ? 
        `${vehicles.length}x ${this.getVehicleTypeDescription(uniqueTypes[0])}` :
        `Mixed fleet (${vehicles.length} vehicles)`
    };
  }

  /**
   * Get emission factor for a specific vehicle type
   * @param {string} vehicleType - Vehicle type identifier
   * @returns {number} Emission factor in kg CO2/km
   */
  getEmissionFactor(vehicleType) {
    if (!vehicleType) return this.emissionFactors.default;
    
    const normalizedType = vehicleType.toLowerCase().replace(/[_\s-]/g, '_');
    
    // Direct match
    if (this.emissionFactors[normalizedType]) {
      return this.emissionFactors[normalizedType];
    }
    
    // Partial matching for common variations
    const typeKeys = Object.keys(this.emissionFactors);
    const matchedKey = typeKeys.find(key => 
      key.includes(normalizedType) || normalizedType.includes(key)
    );
    
    return this.emissionFactors[matchedKey] || this.emissionFactors.default;
  }

  /**
   * Get human-readable description for vehicle type
   * @param {string} vehicleType - Vehicle type identifier
   * @returns {string} Human-readable description
   */
  getVehicleTypeDescription(vehicleType) {
    const descriptions = {
      'van': 'Van',
      'light_truck': 'Light Truck', 
      'pickup': 'Pickup Truck',
      'small_van': 'Small Van',
      'truck': 'Truck',
      'medium_truck': 'Medium Truck',
      'box_truck': 'Box Truck',
      'heavy_truck': 'Heavy Truck',
      'articulated_truck': 'Articulated Truck',
      'large_truck': 'Large Truck',
      'delivery_vehicle': 'Delivery Vehicle',
      'courier_vehicle': 'Courier Vehicle',
      'refrigerated_truck': 'Refrigerated Truck',
      'default': 'Commercial Vehicle',
      'unknown': 'Unknown Vehicle'
    };

    const normalizedType = vehicleType?.toLowerCase().replace(/[_\s-]/g, '_') || 'default';
    return descriptions[normalizedType] || descriptions.default;
  }

  /**
   * Format CO2 value with appropriate units
   * @param {number} co2Value - CO2 value in kg
   * @returns {string} Formatted string with units
   */
  formatCO2Value(co2Value) {
    if (co2Value >= 1000) {
      return `${(co2Value / 1000).toFixed(2)} t CO₂`;
    } else if (co2Value >= 1) {
      return `${co2Value.toFixed(1)} kg CO₂`;
    } else {
      return `${(co2Value * 1000).toFixed(0)} g CO₂`;
    }
  }

  /**
   * Calculate carbon footprint for multiple routes
   * @param {Array} routes - Array of route objects
   * @returns {Object} Aggregated carbon footprint data
   */
  calculateTotalFootprint(routes) {
    if (!routes || routes.length === 0) {
      return {
        totalCO2: 0,
        totalDistance: 0,
        averageCO2PerKm: 0,
        formatted: '0.0 kg CO₂',
        routeCount: 0
      };
    }

    let totalCO2 = 0;
    let totalDistance = 0;

    routes.forEach(route => {
      const distanceKm = route.total_distance ? route.total_distance / 1000 : 0;
      const vehicleType = route.vehicleType || route.vehicle_type || 'default';
      
      const footprint = this.calculateRouteFootprint(distanceKm, vehicleType);
      totalCO2 += footprint.totalCO2;
      totalDistance += distanceKm;
    });

    const averageCO2PerKm = totalDistance > 0 ? totalCO2 / totalDistance : 0;

    return {
      totalCO2,
      totalDistance,
      averageCO2PerKm,
      formatted: this.formatCO2Value(totalCO2),
      formattedAverage: this.formatCO2Value(averageCO2PerKm),
      routeCount: routes.length
    };
  }

  /**
   * Get carbon footprint comparison data
   * @param {number} co2Value - CO2 value in kg
   * @returns {Object} Comparison data with real-world equivalents
   */
  getCarbonComparison(co2Value) {
    return {
      // Trees needed to offset (1 tree absorbs ~21.8 kg CO2/year)
      treesPerYear: Math.ceil(co2Value / 21.8),
      
      // Equivalent car miles (average car emits 0.404 kg CO2/mile)
      carMiles: Math.round(co2Value / 0.404),
      
      // Equivalent household electricity (0.92 kg CO2/kWh average)
      electricityKwh: Math.round(co2Value / 0.92),
      
      // Percentage of average daily personal carbon footprint (16.4 kg CO2/day)
      dailyFootprintPercent: ((co2Value / 16.4) * 100).toFixed(1)
    };
  }
}

export default new CarbonFootprintCalculator();
